import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';

import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { useNavigate } from 'react-router-dom';

import { CookieManager } from '../../utils/cookieManager';
import { debugApiRequests } from '../../utils/debugApi';
import { TokenUtils } from '../../utils/tokenUtils';
import { useToast } from 'hooks/useToast';
import { apiOrchestrator, apiStrapi, setupStrapiHeaders } from 'services/apiService';
import { UserData } from 'types/userData';
import { UserPermissions } from 'types/userPlanFeaturesTypes';
import { clearHeaders, validateAndConfigureHeaders } from 'utils/headersManager';

interface UserContextProps {
  user: UserData | null;
  setUser: React.Dispatch<React.SetStateAction<UserData | null>>;
  token: string | null;
  setToken: (token: string) => void;
  setTokenSafe: (value: string | null, reason: string) => void;
  logout: () => void;
  handleSetUserId: (userId: string) => void;
  permissions: UserPermissions;
  setPermissions: (permissions: UserPermissions) => void;
  initializeUserContext: (userId: string, token: string) => Promise<void>;
  isLoading: boolean;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { toastError } = useToast();

  // Não usar useRefreshToken aqui para evitar dependência circular
  // O refresh será feito via interceptor do Axios

  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    // Tentar obter token do cookie primeiro, depois do localStorage
    const cookieToken = CookieManager.getAccessToken();
    const localToken = localStorage.getItem('AUTH_TOKEN');
    return cookieToken || localToken;
  });
  const [userId, setUserId] = useState<string | null>(() => localStorage.getItem('user_id'));
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const [isLoading, setIsLoading] = useState(true);
  const isLoggingOutRef = useRef(false); // Flag global para evitar múltiplos logouts

  useEffect(() => {
    if (token && userId) {
      apiOrchestrator.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token, userId]);

  // Interceptadores para controlar o loading global
  useEffect(() => {
    const instance = apiStrapi.getInstance();

    const requestInterceptor = instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        setIsLoading(true);
        return config;
      },
      (error: AxiosError) => {
        setIsLoading(false);
        return Promise.reject(error);
      }
    );

    const responseInterceptor = instance.interceptors.response.use(
      (response: AxiosResponse) => {
        setIsLoading(false);
        return response;
      },
      (error: AxiosError) => {
        setIsLoading(false);

        // Tratar erros específicos sem interferir com outros interceptors
        if (error.response?.status === 401) {
          console.warn('[UserContext] Erro 401 detectado, mas não fazendo logout automático');
        }

        return Promise.reject(error);
      }
    );

    return () => {
      instance.interceptors.request.eject(requestInterceptor);
      instance.interceptors.response.eject(responseInterceptor);
    };
  }, []);

  const handleSetUserId = (newUserId: string) => {
    setUserId(newUserId);
    localStorage.setItem('user_id', newUserId);
    console.log('[UserProvider] user_id salvo:', newUserId);
  };

  // Função para setar o token com log do motivo (memoizada)
  const setTokenSafe = useCallback(
    (value: string | null, reason: string) => {
      console.log('[UserContext] setTokenSafe chamado:', {
        hasValue: !!value,
        reason,
        previousToken: !!localStorage.getItem('AUTH_TOKEN'),
        currentPath: window.location.pathname,
      });

      setToken(value);
      if (value) {
        localStorage.setItem('AUTH_TOKEN', value);
        // Também salvar no cookie
        CookieManager.setAccessToken(value);
        console.log(
          '[UserProvider] Token salvo no localStorage e cookie:',
          value,
          '| Motivo:',
          reason
        );
      } else {
        localStorage.removeItem('AUTH_TOKEN');
        // Limpar cookie também
        CookieManager.removeAccessToken();
        console.log('[UserProvider] Token removido do localStorage e cookie! | Motivo:', reason);
      }
    },
    [setToken]
  );

  const initializeUserContext = async (userId: string, token: string) => {
    console.log('[UserContext] Inicializando contexto do usuário...', {
      userId,
      hasToken: !!token,
      currentPath: window.location.pathname,
    });

    // Debug das configurações da API
    debugApiRequests();

    try {
      setIsLoading(true);

      // 1. Configurar token e userId
      setTokenSafe(token, 'login');
      handleSetUserId(userId);

      // 2. Obter headers necessários do localStorage
      const organizationUUID = localStorage.getItem('x-organization-uuid');
      const cognitoUserPool = localStorage.getItem('x-cognito-userpool');

      console.log('[UserContext] Headers do localStorage:', {
        hasOrganizationUUID: !!organizationUUID,
        hasCognitoUserPool: !!cognitoUserPool,
      });

      if (!organizationUUID || !cognitoUserPool) {
        console.error('[UserContext] Headers necessários não encontrados:', {
          organizationUUID,
          cognitoUserPool,
        });
        throw new Error('Headers necessários não encontrados');
      }

      // 3. Configurar headers do Strapi
      if (!setupStrapiHeaders(token, organizationUUID, cognitoUserPool)) {
        console.error('[UserContext] Falha ao configurar headers do Strapi');
        throw new Error('Falha ao configurar headers do Strapi');
      }

      // 4. Configurar headers das outras APIs
      const isValid = await validateAndConfigureHeaders({
        token,
        organizationUUID,
        cognitoUserPool,
      });

      if (!isValid) {
        console.error('[UserContext] Falha ao configurar headers das APIs');
        throw new Error('Failed to configure headers');
      }

      // 5. Buscar dados do usuário
      console.log('[UserContext] Buscando dados do usuário...');
      const userData = await apiOrchestrator.get(`/auth/me/${userId}`);
      console.log('[UserContext] Dados do usuário recebidos:', userData.data);

      // Definir usuário diretamente com os dados do orchestrator
      setUser(userData.data);

      // 7. Emitir evento para que MenuContext possa carregar o menu
      console.log('[UserContext] Emitindo evento userContextInitialized');
      const event = new CustomEvent('userContextInitialized', {
        detail: {
          userId,
          token,
          organizationUUID,
          cognitoUserPool,
        },
      });
      window.dispatchEvent(event);

      console.log('[UserContext] Contexto do usuário inicializado com sucesso!');
    } catch (error) {
      console.error('[UserContext] Erro ao inicializar o contexto do usuário:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Erro desconhecido',
        stack: error instanceof Error ? error.stack : undefined,
        userId,
        hasToken: !!token,
        currentPath: window.location.pathname,
      });
      toastError('Erro ao carregar os dados do usuário.');
      logout();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      try {
        await apiOrchestrator.post('/auth/logout');
      } catch (apiError) {
        console.warn('Falha ao chamar /logout na API:', apiError);
      }

      // Enviar evento de logout
      window.dispatchEvent(new CustomEvent('logout'));

      // Limpar dados do usuário
      setUser(null);
      setTokenSafe(null, 'logout');
      setUserId(null);

      // Limpar localStorage
      localStorage.removeItem('userData');
      localStorage.removeItem('AUTH_TOKEN');
      localStorage.removeItem('user_id');
      localStorage.removeItem('x-cognito-userpool');
      localStorage.removeItem('x-organization-uuid');
      localStorage.removeItem('menuData');
      localStorage.removeItem('selectedMenus');

      // Limpar cookies de autenticação
      CookieManager.clearAllAuthCookies();

      // Limpar headers das APIs
      clearHeaders();

      // Redirecionar para login
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Erro ao realizar logout:', error);
      toastError('Erro ao encerrar sessão. Por favor, tente novamente.');
    }
  }, [navigate, toastError, setTokenSafe]);

  useEffect(() => {
    const storedToken = localStorage.getItem('AUTH_TOKEN');
    const storedUserId = localStorage.getItem('user_id');

    console.log('[UserContext] useEffect inicialização:', {
      hasStoredToken: !!storedToken,
      hasStoredUserId: !!storedUserId,
      currentToken: !!token,
      isLoading,
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString(),
    });

    if (storedToken && storedUserId) {
      console.log('[UserContext] Iniciando contexto com dados do localStorage');
      initializeUserContext(storedUserId, storedToken).catch(error => {
        console.error('[UserContext] Erro na inicialização:', error);
        logout();
      });
    } else {
      console.log('[UserContext] Sem dados no localStorage, não inicializando');
      setIsLoading(false);
    }
  }, []); // Dependência vazia para executar apenas uma vez

  // Verificar saúde do token periodicamente (sem refresh automático para evitar dependência circular)
  useEffect(() => {
    console.log('[UserContext] 🔍 Configurando verificação periódica de saúde do token...', {
      interval: '60000ms',
      timestamp: new Date().toISOString(),
    });

    const checkTokenHealthInterval = setInterval(() => {
      if (token) {
        const isValid = TokenUtils.validateToken(token);
        console.log('[UserContext] 🔍 Verificação de saúde do token:', {
          isValid: isValid.isValid,
          isExpired: isValid.isExpired,
          timeRemaining: isValid.timeUntilExpiry,
          timestamp: new Date().toISOString(),
        });

        if (!isValid.isValid) {
          console.log(
            '[UserContext] ⚠️ Token inválido detectado, mas refresh será feito via interceptor...'
          );
          // O refresh será feito automaticamente pelo interceptor do Axios quando houver 401
        }
      } else {
        console.log('[UserContext] ℹ️ Nenhum token para verificar');
      }
    }, 60000); // Verificar a cada minuto

    return () => {
      console.log('[UserContext] 🧹 Limpando intervalo de verificação de saúde do token');
      clearInterval(checkTokenHealthInterval);
    };
  }, [token, logout]);

  // Listener para logout forçado (quando refresh token falha)
  useEffect(() => {
    const handleForceLogout = (event: CustomEvent) => {
      console.log('[UserContext] 🚨 Logout forçado detectado:', {
        reason: event.detail?.reason || 'Motivo não especificado',
        error: event.detail?.error || 'Nenhum erro específico',
        timestamp: event.detail?.timestamp || new Date().toISOString(),
      });

      // Executar logout imediatamente
      logout();
    };

    // Adicionar listener para o evento de logout forçado
    window.addEventListener('forceLogout', handleForceLogout as EventListener);

    return () => {
      // Remover listener ao desmontar
      window.removeEventListener('forceLogout', handleForceLogout as EventListener);
    };
  }, [logout]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        token,
        setToken,
        setTokenSafe,
        logout,
        handleSetUserId,
        permissions,
        setPermissions,
        initializeUserContext,
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser deve ser usado dentro de um UserProvider');
  }
  return context;
};
